import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import PageHeader from '@Guidelines/Layout/PageHeader';
import SubjectTypeEditActions from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/SubjectTypeEditActions';
import SubjectTypeDefinitionFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/SubjectTypeDefinitionFormData';
import type SubjectTypeGrantsFormData from '@UI/Pages/Administration/SubjectTypes/Shared/FormData/SubjectTypeGrantsFormData';
import AppearanceSection from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Appearance';
import AboutSection from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/About';
import SharingSection from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Sharing';
import SheetSection from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Sheet';
import SpeakerRolesSection from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/SpeakerRoles';
import UsableBySection from '@UI/Pages/Administration/SubjectTypes/Shared/UsableBy';
import WordingSection from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Wording';
import WorkflowsSection from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Workflows';
import type SubjectTypeDefinitionView from '@UI/Store/SubjectTypeDefinitions/Views/SubjectTypeDefinitionView';
import * as React from 'react';
import { ItemBreadcrumb } from '@verclam/breadcrumbs';
import { Message } from '@verclam/ui-kit';

type Props = {
	/** Null when composing a new type. */
	readonly definition: SubjectTypeDefinitionView | null;
};

/**
 * The editor of one type. The draft starts from the loaded definition and is never re-seeded: the
 * parent re-keys this component when the type changes or is archived or restored. An archived type
 * is displayed read-only.
 */
const SubjectTypeEditor: React.FC<Props> = ({ definition }) => {
	const [formData, setFormData] = React.useState(() =>
		definition === null
			? SubjectTypeDefinitionFormData.createEmpty()
			: SubjectTypeDefinitionFormData.fromView(definition),
	);
	const handleGrantsChange = React.useCallback(
		(update: (grants: SubjectTypeGrantsFormData) => SubjectTypeGrantsFormData) => {
			setFormData((current) => current.updateGrants(update));
		},
		[],
	);
	const isReadOnly = definition?.isArchived ?? false;
	const isUsed = definition?.isUsed ?? false;
	const title = definition?.wording.plural ?? 'Nouveau type de dossier';

	return (
		<React.Fragment>
			<ItemBreadcrumb label={title} />
			<PageHeader
				eyebrow="CONFIGURATION"
				title={title}
				actions={
					<SubjectTypeEditActions
						formData={formData}
						onChange={setFormData}
						definition={definition}
					/>
				}
			/>
			<ConditionalLoader data={isReadOnly}>
				<Message severity="info" text="Ce type est archivé : restaurez-le pour le modifier." />
			</ConditionalLoader>
			<WordingSection formData={formData} onChange={setFormData} isReadOnly={isReadOnly} />
			<AppearanceSection formData={formData} onChange={setFormData} isReadOnly={isReadOnly} />
			<SheetSection
				formData={formData}
				onChange={setFormData}
				isReadOnly={isReadOnly}
				isUsed={isUsed}
			/>
			<AboutSection formData={formData} onChange={setFormData} isReadOnly={isReadOnly} />
			<UsableBySection grants={formData.grants} onChange={handleGrantsChange} isReadOnly={isReadOnly} />
			<SharingSection formData={formData} onChange={setFormData} isReadOnly={isReadOnly} />
			<SpeakerRolesSection formData={formData} onChange={setFormData} isReadOnly={isReadOnly} />
			<WorkflowsSection formData={formData} onChange={setFormData} isReadOnly={isReadOnly} />
		</React.Fragment>
	);
};

export default SubjectTypeEditor;
